import config from "@/v2-backoffice-client/config"
import type { DotProps } from "@/v2-backoffice-client/components/dot/Dot"

// The Features page exists only where v2-features-db is reachable: the
// authoring DB lives solely on the DEV SQL server (there is no prod
// variant), so backoffice deployments on the prod network (ENV_TYPE=prod:
// staging + release) could never load the page — hide it entirely there
// instead of shipping a permanently-erroring one. Gates both the sidebar
// item and the routes.
export const IS_TRANSLATION_ENABLED = config.envType === "dev"

// The page's route. It was /translation for as long as text was the only
// facet it edited; the row now also edits configuration, access and country
// availability, so the surface is named after the thing rather than the one
// job. Old links keep working (AppRouter redirects /translation/*).
export const FEATURE_PAGE_PATH = "/features"

// The translation catalog's own route, for the surfaces that link an
// operator into the publish queue (every catalog sheet's Details tab does).
export const TRANSLATION_PENDING_PATH = `${FEATURE_PAGE_PATH}/pending`

// `id` of the tab strip inside an expanded catalog row. Rides in the
// `<id>-tab` search param (see components/tabs useTabParam), so the facet in
// view survives a reload and can be linked to.
export const FEATURE_ROW_TABS_ID = "feature-row"

// AI self-reported confidence -> Dot variant, shared by the row editor and
// the Add objects preview. Unknown values fall back to "muted".
export const CONFIDENCE_DOT_VARIANT: Record<string, DotProps["variant"]> = {
  high: "success-1",
  medium: "warning",
  low: "destructive",
}

// UI display order for languages across the Translation tab — the table
// columns and the per-language editors iterate the single list built by
// the board pass, so ordering it here orders the whole tab. English first
// (the default + fallback), then Bokmål, Nynorsk, Swedish, Danish. Any
// language not listed is appended after, ordered by label, so a newly
// added language still shows without a code change.
export const LANGUAGE_DISPLAY_ORDER = [
  "en-GB",
  "nb-NO",
  "nn-NO",
  "sv-SE",
  "da-DK",
]

// The catalog's canonical language: the meaning anchor for AI suggestions
// and the side compared against code fallbacks.
export const ENGLISH_LANGUAGE_CODE = "en-GB"

// The second language the Add-text dialog seeds directly (the operators'
// own); the remaining languages flow through the Missing translations queue.
export const NORWEGIAN_BOKMAL_LANGUAGE_CODE = "nb-NO"

// Source language options for pasted type-value tables (Geonorge
// catalogues are Norwegian).
export const ADD_OBJECTS_SOURCE_LANGUAGES = ["nb-NO", "en-GB", "sv-SE"] as const

// Every routed view of the Features page. Each is its own URL:
// /features/<view>; the catalog is /features/catalog (+ ?category=<key>).
// The ids are URL-stable and predate the current labels: "incomplete"
// renders as "Missing translations", "missing" as "Missing in database",
// "orphaned" as "Missing in code", "fallback" as "Pending code sync".
// "add-objects" lives in the sidebar's Tools section as "Add type values".
export const TRANSLATION_VIEWS = [
  "catalog",
  "incomplete",
  "missing",
  "orphaned",
  "fallback",
  "duplicates",
  "add-objects",
  "languages",
  "countries",
  "pending",
] as const

export type TranslationView = (typeof TRANSLATION_VIEWS)[number]

export const TRANSLATION_CATEGORY_PARAM = "category"

// The two kinds of row the catalog holds, in the words used wherever an
// operator picks between them (the Kind pills, the Add entry dialog's Kind
// select). Plural where a pill counts rows, so the labels live next to each
// other rather than being restated per surface.
//
// They are genuinely different objects, not a presentation split: a FEATURE is
// a UI text keyed by FeatureCode, a TYPE VALUE is a data label keyed by
// '<TableName>|<Code>' and carries the Attributes the runtime seed reads.
// Sharing one list is what made the catalog hard to read.
export const OBJECT_TYPE_LABEL = {
  Feature: "Feature",
  TypeValue: "Data label",
} as const

export const OBJECT_TYPE_LABEL_PLURAL = {
  Feature: "Features",
  TypeValue: "Data labels",
} as const

// URL search param carrying the active Kind pill. Distinct from the Status
// pills' default `status`, so the two groups round-trip independently.
export const TRANSLATION_KIND_PARAM = "kind"
